<template>
  <b-modal
    id="create-pool-modal"
    size="lg"
    :title="
      poolToEdit
        ? $t('portfolioManager.createPoolModal.titleEdit')
        : $t('portfolioManager.createPoolModal.title')
    "
    @shown="checkForEdit"
    :cancel-title="$t('general.cancel')"
    cancel-variant="primary"
    @hidden="handleHide"
    centered
  >
    <p v-if="!poolToEdit">
      {{ $t("portfolioManager.createPoolModal.description") }}
    </p>
    <validation-observer ref="poolForm">
      <form>
        <validation-provider
          v-slot="{ failed, errors }"
          rules="required"
          name="title"
        >
          <simple-form-input
            :hasError="failed"
            :helperText="errors[0]"
            :label="$t('portfolioManager.createPoolModal.poolTitle')"
            v-model="name"
          />
        </validation-provider>
        <validation-provider v-slot="{ failed, errors }" name="description">
          <simple-form-input
            :hasError="failed"
            :helperText="errors[0]"
            :label="$t('portfolioManager.createPoolModal.poolDescription')"
            v-model="description"
          />
        </validation-provider>
      </form>
    </validation-observer>
    <p class="body-bold">
      {{ $t("portfolioManager.createPoolModal.poolAssets") }}
    </p>
    <div class="radio-group">
      <radio-button
        :id="'file'"
        :name="'inputType'"
        :label="$t('portfolioManager.createPoolModal.uploadFromFile')"
        v-model="inputType"
        v-if="!poolToEdit"
      />
      <radio-button
        :id="'manual'"
        :label="$t('portfolioManager.createPoolModal.chooseManually')"
        :name="'inputType'"
        v-model="inputType"
      />
    </div>
    <dual-select-box
      :options="tickersOptions"
      :fields="[
        {
          key: 'ticker',
          label: $t('portfolioManager.createPoolModal.assetsInPool'),
        },
      ]"
      @fillPortfolioData="handleSetPool"
      :initialItems="initialPool"
      v-if="inputType === 'manual'"
    />
    <div class="file-upload" v-else>
      <p class="file-name" v-if="file">
        <upload-icon :size="20" /><span class="txt">{{ file.name }}</span>
        <delete-icon :size="20" @click="file = null" />
      </p>
      <drop-zone v-else @fileUploaded="handleFile" id="cm" />
      <p class="format-hint">
        {{ $t("portfolioManager.createPoolModal.poolFile") }}
      </p>
    </div>
    <template v-slot:modal-footer>
      <b-button variant="primary" @click="handleBack">
        {{ $t("general.buttons.prev") }}
      </b-button>
      <b-button
        variant="secondary"
        :disabled="isCreationDisabled"
        @click="handleCreatePool"
      >
        <b-spinner small v-if="isCreatingPool" />
        {{
          poolToEdit
            ? $t("general.edit")
            : $t("portfolioManager.createPoolModal.create")
        }}
      </b-button>
    </template>
  </b-modal>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from "vuex";
import DeleteIcon from "vue-material-design-icons/Delete.vue";
import DropZone from "../DropZone.vue";
import DualSelectBox from "../inputs/DualSelectBox.vue";
import MainAlert from "../alerts/MainAlert.vue";
import RadioButton from "../inputs/RadioButton.vue";
import SimpleFormInput from "../inputs/SimpleFormInput.vue";
import UploadIcon from "vue-material-design-icons/FileUploadOutline.vue";
export default {
  name: "create-pool-modal",
  components: {
    SimpleFormInput,
    DualSelectBox,
    RadioButton,
    DropZone,
    UploadIcon,
    DeleteIcon,
  },
  data() {
    return {
      name: "",
      description: "",
      pool: [],
      initialPool: [],
      initialName: "",
      inputType: "file",
      file: null,
      isCreatingPool: false,
    };
  },
  computed: {
    ...mapGetters("portfolioManagerStore", {
      sp100: "getPoolSP100",
      poolToEdit: "poolToEdit",
      pools: "pools",
      availableTickers: "availableTickers",
    }),
    tickersOptions() {
      return this.availableTickers
        ? Object.entries(this.availableTickers).map(([key, val]) => ({
            ticker: key,
            icon: val,
          }))
        : [];
    },
    isCreationDisabled() {
      return (
        (this.inputType === "file" && !this.file) ||
        (this.inputType === "manual" && this.pool.length === 0) ||
        (this.poolToEdit &&
          this.name === this.initialName &&
          JSON.stringify([...this.pool].sort()) ===
            JSON.stringify(this.initialPool.map(({ ticker }) => ticker).sort()))
      );
    },
  },
  methods: {
    ...mapActions("portfolioManagerStore", ["createPool", "editPool"]),
    ...mapMutations("portfolioManagerStore", ["setPoolToEdit"]),
    handleSetPool(data) {
      this.pool = data.map(({ ticker }) => ticker);
    },
    handleCreatePool(e) {
      e.preventDefault();
      this.$refs.poolForm.validate().then((isValid) => {
        if (isValid) {
          this.isCreatingPool = true;
          const data = {
            name: this.name,
            description: this.description,
          };
          this.file ? (data.pool = this.file) : (data.tickers = this.pool);
          const performPoolTask = () =>
            this.poolToEdit
              ? this.editPool({
                  poolData: data,
                  id: this.poolToEdit,
                })
              : this.createPool(data);

          performPoolTask()
            .then(() => {
              this.$toast.success({
                component: MainAlert,
                props: {
                  message: this.$t(
                    `portfolioManager.createPoolModal.pool${
                      this.poolToEdit ? "Edited" : "Created"
                    }`
                  ),
                },
              });
              this.$bvModal.hide("create-pool-modal");
            })
            .catch((err) => {
              const errors = {
                400: this.$t("errors.missingData"),
                409: this.$t("errors.nameExists"),
                415: this.$t("errors.wrongFile"),
              };
              this.$toast.error({
                component: MainAlert,
                props: {
                  message: errors[err.response.status],
                },
              });
            })
            .finally(() => (this.isCreatingPool = false));
        }
      });
    },
    checkForEdit() {
      if (this.poolToEdit !== null) {
        this.inputType = "manual";
        this.$nextTick(() => {
          const pool = this.pools.find(
            (pool) => pool.pool_id === this.poolToEdit
          );
          this.name = pool.name;
          this.initialName = pool.name;
          this.description = pool.description;
          this.initialPool = pool.tickers.map(({ ticker }) => ({ ticker }));
        });
      }
    },
    handleBack() {
      this.$bvModal.hide("create-pool-modal");
      this.$bvModal.show("pools-modal");
    },
    handleFile(data) {
      this.file = data;
    },
    handleHide() {
      this.file = null;
      this.name = "";
      this.description = "";
      this.pool = [];
      this.initialPool = [];
      this.inputType = "file";
      this.setPoolToEdit(null);
    },
  },
  watch: {
    poolToEdit() {
      this.checkForEdit();
    },
  },
};
</script>

<style lang="scss" scoped>
.format-hint {
  color: $subtleText;
  margin-bottom: 0;
  margin-top: 0.5rem;
}
.file-name {
  color: $primaryColor;
  display: flex;
  align-items: center;
  .txt {
    margin: 0 0.5rem 0 0.2rem;
  }
  .delete-icon {
    cursor: pointer;
  }
}
.radio-group {
  display: flex;
}
</style>